import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { LocalStorageKey, localStorageSet } from '../data/client_local_storage.ts';
import { synchronizeOrgsWithServer, useCurrentOrg } from '../data/use_current_org.tsx';
import { synchronizeHouseholdWithServer, useHousehold } from '../data/use_household.tsx';
import { useEffectOnce } from '../util/use_effect_once.ts';
import { useOnline } from '../util/use_online.tsx';
import { Button } from './onboarding/button.tsx';
import {
  ChurchIdTokenQueryString,
  finishOnboarding,
  goToNextOnboardingStep,
  Message,
  OrgIdTokenQueryString,
  setHouseholdOriginBasedOnOrgType,
} from './onboarding/common.tsx';
import { Frame } from './onboarding/frame.tsx';

export function HouseholdInvite() {
  const { search } = useLocation();
  const { household } = useHousehold();
  const householdToken = household?.token;
  const urlParams = new URLSearchParams(search);
  const orgId = Number(urlParams.get(OrgIdTokenQueryString) ?? urlParams.get(ChurchIdTokenQueryString));
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState(false);
  const { currentOrg, setHouseholdOrg } = useCurrentOrg();
  const navigate = useNavigate();
  const isOnline = useOnline();

  useEffectOnce(() => {
    if (isNaN(orgId)) {
      setError(true);
      return;
    }

    if (!isOnline) {
      setError(true);
      return;
    }

    void (async () => {
      try {
        const org = await server_api.getOrg(orgId);
        if (!org) {
          setError(true);
          return;
        }
        if (!householdToken) {
          localStorageSet(LocalStorageKey.OnboardingOrg, org);
          setHouseholdOriginBasedOnOrgType(org.type);
          await goToNextOnboardingStep(navigate, { from: Pages.HouseholdInvite, action: 'noHousehold' });
          return;
        }
        await setHouseholdOrg(org);
        await synchronizeOrgsWithServer();
        await synchronizeHouseholdWithServer();
        // the invite is where this onboarding ends, even if the visitor never clicks CONTINUE
        await finishOnboarding();
        setFinished(true);
      } catch {
        setError(true);
      }
    })();
  });

  const onContinue = useCallback(async () => {
    await goToNextOnboardingStep(navigate, { from: Pages.HouseholdInvite, action: 'continue' });
  }, [navigate]);

  if (error) {
    const href = `mailto:support@crescendosw.com?subject=Missing org id for household invite&body=URL: ${window.location.href}`;
    const mainContent = isOnline ? (
      <Message $widthInChars={20}>
        An internal error has occurred. <MailLink href={href}>Click here</MailLink> to reach out to support.
      </Message>
    ) : (
      <Message $widthInChars={20}>Please check your Internet connection and click the invitation again.</Message>
    );
    return (
      <Frame
        onboardingPage={undefined}
        mainContent={mainContent}
        footerContent={<Button onClick={onContinue}>CONTINUE</Button>}
        onEnterKeypress={onContinue}
      />
    );
  }

  if (!finished || !currentOrg) {
    return (
      <Frame
        onboardingPage={undefined}
        mainContent={<Message $widthInChars={24}>Updating household church...</Message>}
        footerContent={<div />}
      />
    );
  }

  return (
    <Frame
      onboardingPage={undefined}
      mainContent={
        <Message $widthInChars={24}>
          Congratulations!
          <br />
          <br />
          Your household is associated with
          <br />
          {currentOrg.name}.
        </Message>
      }
      footerContent={<Button onClick={onContinue}>CONTINUE</Button>}
      onEnterKeypress={onContinue}
      suppressBackButton={true}
    />
  );
}

const MailLink = styled.a`
  color: blue;
  text-decoration: underline;
`;
